/**
 * 知识点引擎 —— 对应技术设计 §8「上下文知识触发」。
 *
 * 来源：`bug/023-化学/001-bug.md` 第 3 条「知识点好，错误提示没有，用户不知道该如何使用学习」。
 * 根因是引擎侧的知识点通道从来没接到 UI 上。这个文件补的是「触发 → 排序 → 去重」，
 * 渲染在 app.ts。
 *
 * §8 的触发优先级，逐条落在 `rank()`：
 *   1 安全风险优先
 *   2 当前步骤相关知识优先
 *   3 重复错误升级解释深度
 *   4 已掌握知识减少打扰
 */

export type KnowledgeLevel = "safety" | "key" | "normal";

export interface KnowledgeItem {
  id: string;
  title: string;
  content: string;
  level?: KnowledgeLevel;
  display?: "side_panel" | "modal" | "inline";
  /** 关联考点，供 M1c 的考点面板与红线 10 的证据回溯用 */
  examPointId?: string;
}

export interface KnowledgeDocument {
  schemaVersion?: string;
  id?: string;
  items?: KnowledgeItem[];
}

export type KnowledgeSource = "rule" | "safety" | "step_error";

export interface KnowledgeShow {
  item: KnowledgeItem;
  source: KnowledgeSource;
  /** 第几次看到这一条。§8「重复错误升级解释深度」用它 */
  seenCount: number;
}

const LEVEL_WEIGHT: Record<KnowledgeLevel, number> = { safety: 3, key: 2, normal: 1 };
const SOURCE_WEIGHT: Record<KnowledgeSource, number> = { safety: 3, step_error: 2, rule: 1 };

export class KnowledgeEngine {
  private readonly items = new Map<string, KnowledgeItem>();
  private readonly seen = new Map<string, number>();
  /** 已经掌握（连续多次未再触发同类错误）的条目，降低打扰 */
  private readonly mastered = new Set<string>();

  constructor(documents: readonly KnowledgeDocument[]) {
    for (const doc of documents) {
      for (const item of doc.items ?? []) this.items.set(item.id, item);
    }
  }

  has(id: string): boolean {
    return this.items.has(id);
  }

  get(id: string): KnowledgeItem | undefined {
    return this.items.get(id);
  }

  listIds(): string[] {
    return [...this.items.keys()];
  }

  /**
   * 触发一条知识点。返回 null 表示不展示：
   * 要么 id 不存在（**这本身是实验包缺陷**，调用方应报出来而不是吞掉），
   * 要么已掌握且不是安全级。
   */
  trigger(id: string, source: KnowledgeSource): KnowledgeShow | null {
    const item = this.items.get(id);
    if (!item) return null;

    const seenCount = (this.seen.get(id) ?? 0) + 1;
    this.seen.set(id, seenCount);

    // §8 第 4 条：已掌握的减少打扰。但安全级永远要弹 —— 安全不打折。
    if (this.mastered.has(id) && item.level !== "safety") return null;

    return { item, source, seenCount };
  }

  markMastered(id: string): void {
    this.mastered.add(id);
  }

  /** 同一轮里触发了多条时，按 §8 的优先级排序，取最该说的那条先说。 */
  rank(shows: readonly KnowledgeShow[]): KnowledgeShow[] {
    return [...shows].sort((a, b) => {
      const levelDiff =
        LEVEL_WEIGHT[b.item.level ?? "normal"] - LEVEL_WEIGHT[a.item.level ?? "normal"];
      if (levelDiff !== 0) return levelDiff;
      const sourceDiff = SOURCE_WEIGHT[b.source] - SOURCE_WEIGHT[a.source];
      if (sourceDiff !== 0) return sourceDiff;
      return b.seenCount - a.seenCount;
    });
  }
}

/**
 * 校验：mission / rules 里引用的知识点 id 必须都能解析到。
 * 解析不到就是实验包缺陷 —— 学生会遇到「触发了但什么都没弹出来」，
 * 而这类静默失败正是 `bug/023` 第 3 条的体感来源。
 */
export function findMissingKnowledgeIds(
  engine: KnowledgeEngine,
  referenced: readonly string[],
): string[] {
  return [...new Set(referenced)].filter((id) => !engine.has(id));
}
